/**
 * Agent Builder — Library view (Phase 2, T2.5 + catalog parity).
 *
 * Three sub-tabs (Agent Studio parity):
 *   • Workflows  — stored :AgentWorkflow rows (GET /api/agent-workflows); each links
 *                  into Build (edit) or Run (execute), or can be deleted.
 *   • Agents     — the reusable agent catalog (GET /api/agents).
 *   • Guardrails — the reusable guardrail catalog (GET /api/guardrails).
 *
 * The cockpit appears ONLY when VITE_ENABLE_AGENT_BUILDER is on (the route is
 * registered behind that flag in App.tsx). Additive: reuses the existing UI
 * primitives (Button / Badge / Card) and the typed client.
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Pencil, Play, RefreshCw, FilePlus2, Trash2, LayoutGrid, Bot, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { agentBuilderApi } from '../../lib/api/agentBuilderApi';
import { SESSION_CACHE } from '../../lib/queryCache';
import { TIER_LABEL } from '../../components/agent-builder/llmTiers';
import type { AgentDef, AgentWorkflow, GuardrailDef, GuardrailKind } from '../../types/agentBuilder';

type CatalogTab = 'workflows' | 'agents' | 'guardrails';

const ROLE_ICON: Record<string, string> = {
  orchestrator: '◎', preprocessor: '▤', retriever: '⤓', extractor: '⛏', analyzer: '📊',
  specialist: '★', researcher: '🔎', strategist: '♟', validator: '✔', refiner: '↻', critic: '⚖', output: '⎙',
};

function statusBadge(status: AgentWorkflow['status']) {
  return status === 'published' ? (
    <Badge variant="success" size="sm">published</Badge>
  ) : (
    <Badge variant="warning" size="sm">draft</Badge>
  );
}

function kindVariant(kind: GuardrailKind) {
  if (kind === 'input') return 'info' as const;
  if (kind === 'output') return 'success' as const;
  return 'warning' as const; // action
}

const TABS: { id: CatalogTab; label: string; icon: React.ElementType }[] = [
  { id: 'workflows', label: 'Workflows', icon: LayoutGrid },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'guardrails', label: 'Guardrails', icon: ShieldCheck },
];

export interface LibraryViewProps {
  /** Open a workflow in the Build canvas. */
  onOpen?: (workflowId: string) => void;
  /** Open a workflow in the Run view. */
  onRun?: (workflowId: string) => void;
  /** Start a brand-new blank workflow in the Build canvas. */
  onNew?: () => void;
}

export const LibraryView: React.FC<LibraryViewProps> = ({ onOpen, onRun, onNew }) => {
  const [tab, setTab] = useState<CatalogTab>('workflows');

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Session-cached lists — no refetch on tab revisit. Refresh via the Refresh
  // button (refetch) or after a delete (refetch). The agents/guardrails catalogs
  // lazy-load the first time their sub-tab is opened, then stay cached.
  const {
    data: workflows = [],
    isLoading: loading,
    refetch: refetchWorkflows,
  } = useQuery<AgentWorkflow[]>({
    queryKey: ['agent-workflows'],
    queryFn: () => agentBuilderApi.listWorkflows(),
    ...SESSION_CACHE,
  });

  const {
    data: agents = null,
    isFetching: agentsFetching,
    refetch: refetchAgents,
  } = useQuery<AgentDef[]>({
    queryKey: ['agent-agents'],
    queryFn: () => agentBuilderApi.listAgents(),
    enabled: tab === 'agents',
    ...SESSION_CACHE,
  });

  const {
    data: guardrails = null,
    isFetching: guardrailsFetching,
    refetch: refetchGuardrails,
  } = useQuery<GuardrailDef[]>({
    queryKey: ['agent-guardrails'],
    queryFn: () => agentBuilderApi.listGuardrails(),
    enabled: tab === 'guardrails',
    ...SESSION_CACHE,
  });

  const loadingCatalog = agentsFetching || guardrailsFetching;

  const refresh = () => {
    if (tab === 'workflows') refetchWorkflows();
    else if (tab === 'agents') refetchAgents();
    else refetchGuardrails();
  };

  const deleteTarget = workflows.find((w) => w.workflowId === deleteId) ?? null;
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await agentBuilderApi.deleteWorkflow(deleteId);
      await refetchWorkflows();
      toast.success('Workflow deleted');
      setDeleteId(null);
    } catch (err) {
      toast.error('Failed to delete workflow');
      // eslint-disable-next-line no-console
      console.error('deleteWorkflow failed', err);
    } finally {
      setDeleting(false);
    }
  };

  const busy = tab === 'workflows' ? loading : loadingCatalog;

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="agent-library-view">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[22px] font-semibold text-neutral-900">Library</h2>
          <p className="mt-0.5 text-[13px] text-neutral-500">Reusable agents, guardrails and saved workflows.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          {tab === 'workflows' && onNew && (
            <Button variant="primary" size="sm" onClick={onNew} aria-label="New workflow">
              <FilePlus2 className="h-4 w-4" /> New workflow
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <nav className="flex gap-1.5" role="tablist" aria-label="Library catalog">
          {TABS.map((t) => {
            const active = t.id === tab;
            const n = t.id === 'workflows' ? workflows.length : t.id === 'agents' ? agents?.length ?? 0 : guardrails?.length ?? 0;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                  active ? 'bg-secondary-50 text-secondary-700 ring-1 ring-secondary-200' : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label} <span className="text-neutral-400">({n})</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* ---- Workflows tab ---- */}
      {tab === 'workflows' && (
        <>
          {loading && workflows.length === 0 && (
            <div className="flex items-center justify-center gap-2 py-16 text-neutral-500" data-testid="library-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading workflows…
            </div>
          )}
          {!loading && workflows.length === 0 && (
            <Card className="p-8 text-center text-neutral-500">
              <span data-testid="library-empty">No workflows yet. Compose one in the Build canvas.</span>
            </Card>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="library-list">
            {workflows.map((wf) => (
              <Card
                key={wf.workflowId}
                className="group flex flex-col rounded-xl p-5 transition-all hover:border-secondary-300 hover:shadow-md"
              >
                <div className="flex items-start gap-3" data-testid={`library-row-${wf.workflowId}`}>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-secondary-50 text-secondary-600">
                    <LayoutGrid className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-neutral-900 transition-colors group-hover:text-secondary-700">{wf.name}</div>
                    <div className="truncate font-mono text-[10.5px] text-neutral-400">{wf.workflowId}</div>
                  </div>
                  {statusBadge(wf.status)}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  <Badge variant="info" size="sm">{wf.strategy}</Badge>
                  {wf.product && <Badge variant="default" size="sm">{wf.product}</Badge>}
                  <Badge variant="neutral" size="sm">v{wf.headVersion ?? 0}</Badge>
                </div>
                <div className="mt-auto flex items-center gap-2 pt-5">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => onOpen?.(wf.workflowId)} aria-label={`Open ${wf.name} in builder`}>
                    <Pencil className="h-3.5 w-3.5" /> Open
                  </Button>
                  <Button variant="primary" size="sm" className="flex-1" onClick={() => onRun?.(wf.workflowId)} aria-label={`Run ${wf.name}`}>
                    <Play className="h-3.5 w-3.5" /> Run
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(wf.workflowId)}
                    aria-label={`Delete ${wf.name}`}
                    title="Delete workflow"
                    className="!border-red-200 !text-red-600 hover:!bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ---- Agents tab ---- */}
      {tab === 'agents' && (
        <>
          {loadingCatalog && agents === null && (
            <div className="flex items-center justify-center gap-2 py-16 text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading agents…
            </div>
          )}
          {agents !== null && agents.length === 0 && (
            <Card className="p-8 text-center text-neutral-500">No agents in the catalog.</Card>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="library-agents">
            {(agents ?? []).map((a) => (
              <Card
                key={a.agentId}
                className="group flex flex-col rounded-xl p-5 transition-all hover:border-secondary-300 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-secondary-50 text-lg text-secondary-700">
                    {ROLE_ICON[a.role] ?? '◆'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[15px] font-semibold text-neutral-900 transition-colors group-hover:text-secondary-700">{a.name}</span>
                      <Badge variant="neutral" size="sm">{TIER_LABEL[a.tier] ?? a.tier}</Badge>
                    </div>
                    <div className="mt-0.5 text-[10.5px] uppercase tracking-wide text-secondary-600">{a.role}</div>
                  </div>
                </div>
                <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-[12.5px] leading-relaxed text-neutral-500">{a.objective || 'No objective described.'}</p>
                {(a.guardrails?.length ?? 0) > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {a.guardrails!.map((g) => (
                      <span key={g} className="rounded-md bg-amber-50 px-2 py-0.5 text-[10.5px] text-amber-700">⛨ {g}</span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 text-[10.5px] text-neutral-400">no guardrails</div>
                )}
                {(a.inputs?.length || a.outputs?.length) ? (
                  <div className="mt-auto border-t border-neutral-100 pt-3 font-mono text-[10.5px] leading-relaxed text-neutral-400">
                    {a.inputs?.length ? <div className="truncate"><span className="text-neutral-300">in:</span> {a.inputs.join(', ')}</div> : null}
                    {a.outputs?.length ? <div className="truncate"><span className="text-neutral-300">out:</span> {a.outputs.join(', ')}</div> : null}
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ---- Guardrails tab ---- */}
      {tab === 'guardrails' && (
        <>
          {loadingCatalog && guardrails === null && (
            <div className="flex items-center justify-center gap-2 py-16 text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading guardrails…
            </div>
          )}
          {guardrails !== null && guardrails.length === 0 && (
            <Card className="p-8 text-center text-neutral-500">No guardrails in the catalog.</Card>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" data-testid="library-guardrails">
            {(guardrails ?? []).map((g) => (
              <Card
                key={g.guardrailId}
                className="group rounded-xl p-5 transition-all hover:border-secondary-300 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-600">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-neutral-900 transition-colors group-hover:text-secondary-700">{g.name}</div>
                    <div className="truncate font-mono text-[10.5px] text-neutral-400">{g.guardrailId}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Badge variant={kindVariant(g.kind)} size="sm">{g.kind}</Badge>
                    {g.severity && (
                      <Badge variant={g.severity === 'block' ? 'error' : 'warning'} size="sm">{g.severity}</Badge>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-500">
                  {g.description || 'No description provided.'}
                </p>
                {g.builtin && (
                  <div className="mt-3 inline-flex items-center gap-1 rounded-md bg-neutral-50 px-2 py-0.5 text-[10.5px] text-neutral-500 ring-1 ring-neutral-200">
                    built-in
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={deleteId !== null}
        title="Delete workflow?"
        message={`Permanently delete "${deleteTarget?.name ?? ''}"? Published versions are removed; run history is preserved. This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onCancel={() => (deleting ? undefined : setDeleteId(null))}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default LibraryView;
